import { configureStore, isRejectedWithValue } from '@reduxjs/toolkit';
import { supportApi } from '@app/api/support';
import { authApi } from '@auth/api/auth';
import { getItem, setItem, APPLICATION_KEY } from '@app/util/cache';
import { userApi } from '@dashboard/api/users';
import { projectsApi } from '@dashboard/api/projects';
import { countriesApi } from '@dashboard/api/countries';
import authReducer, { clearData } from '@auth/slices/auth';
import countriesReducer from '@dashboard/slices/countries';
import projectsReducer from '@dashboard/slices/projects';
import modalReducer, { setErrorModal } from '@app/slices/modals';
import usageStatsReducer from '@dashboard/slices/usageStats';
import resultsStatsReducer from '@dashboard/slices/resultsStats';
import analysisStatsReducer from '@dashboard/slices/analysisStats';
import identificationStatsReducer from '@dashboard/slices/identificationStats';
import resumeStatsReducer from '@dashboard/slices/resumeStats';
import centersReducer from '@dashboard/slices/centers';
import filtersReducer from '@dashboard/slices/filters';
import usersReducer from '@dashboard/slices/users';

import { usageStatsApi } from '@dashboard/api/usageStats';
import { resultsStatsApi } from '@dashboard/api/resultsStats';
import { analysisStatsApi } from '@dashboard/api/analysisStats';
import { identificationStatsApi } from '@dashboard/api/identificationStats';
import { centersApi } from '@dashboard/api/centers';
import { resumeStatsApi } from '@dashboard/api/resumeStats';
import { syncMonitorApi } from '@dashboard/api/syncMonitor';
import syncMonitorReducer from '@dashboard/slices/syncMonitor';

const includeKeys = ['auth', 'countries', 'filters', 'modal', 'projects'];

const localStorageMiddleware = ({ getState }) => {
  return (next) => (action) => {
    const result = next(action);
    const state = getState();

    const keys = Object.keys(state);
    const savedState = {};
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      if (includeKeys.indexOf(key) > -1) {
        savedState[key] = state[key];
      }
    }
    setItem(APPLICATION_KEY, savedState);
    return result;
  };
};

const reHydrateStore = () => {
  const store = getItem(APPLICATION_KEY);
  const newStore = store ? getItem(APPLICATION_KEY) : {};

  return { ...newStore, ...{ modal: { error: null, loading: null } } };
};

const createErrorHandlerMiddleware = (store) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    if (action.payload && action.payload.status) {
      if (action.payload.status === 401) {
        Promise.all([
          store.dispatch(setErrorModal({ data: { message: 'Unauthorized' } })),
          store.dispatch(clearData()),
        ]);
      } else {
        store.dispatch(setErrorModal(action.payload));
      }
    }
  }

  return next(action);
};

export const store = configureStore({
  reducer: {
    [authApi.reducerPath]: authApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [projectsApi.reducerPath]: projectsApi.reducer,
    [countriesApi.reducerPath]: countriesApi.reducer,
    [usageStatsApi.reducerPath]: usageStatsApi.reducer,
    [resultsStatsApi.reducerPath]: resultsStatsApi.reducer,
    [supportApi.reducerPath]: supportApi.reducer,
    [analysisStatsApi.reducerPath]: analysisStatsApi.reducer,
    [identificationStatsApi.reducerPath]: identificationStatsApi.reducer,
    [resumeStatsApi.reducerPath]: resumeStatsApi.reducer,
    [centersApi.reducerPath]: analysisStatsApi.reducer,
    [syncMonitorApi.reducerPath]: syncMonitorApi.reducer,
    auth: authReducer,
    modal: modalReducer,
    projects: projectsReducer,
    countries: countriesReducer,
    usageStats: usageStatsReducer,
    resultsStats: resultsStatsReducer,
    filters: filtersReducer,
    analysisStats: analysisStatsReducer,
    identificationStats: identificationStatsReducer,
    resumeStats: resumeStatsReducer,
    centers: centersReducer,
    users: usersReducer,
    syncMonitor: syncMonitorReducer,
  },
  preloadedState: reHydrateStore(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(
      localStorageMiddleware,
      authApi.middleware,
      userApi.middleware,
      projectsApi.middleware,
      usageStatsApi.middleware,
      resultsStatsApi.middleware,
      countriesApi.middleware,
      analysisStatsApi.middleware,
      identificationStatsApi.middleware,
      resumeStatsApi.middleware,
      centersApi.middleware,
      syncMonitorApi.middleware,
      supportApi.middleware,
      createErrorHandlerMiddleware.bind(this),
    ),
});
